import { client } from '@/api/common';
import {
  API,
  BASE,
  URL_SUPPORT_ATTACHMENTS,
  URL_SUPPORT_ATTACHMENT_FINALIZE,
  URL_SUPPORT_ATTACHMENT_REQUEST,
  URL_SUPPORT_DOWNLOAD,
  URL_SUPPORT_MESSAGES,
  URL_SUPPORT_SLOTS_DAYS,
  URL_SUPPORT_SLOTS_HOURS,
  URL_SUPPORT_STATUS,
  URL_SUPPORT_TICKETS,
  URL_TENANTS,
  V1,
} from '@/lib/constants/urls';
import type {
  CreateSupportTicketDto,
  CreateSupportTicketResultDto,
  ListResponse,
  SlotDayResponse,
  SlotHourResponse,
  SupportTicketType,
  UploadFinalizeDto,
  UploadFinalizeResponse,
  UploadRequestDto,
  UploadRequestResponse,
} from '@repo/api';
import {
  buildSupportTicketFormData,
  buildSupportTicketMessageFormData,
} from './build-support-ticket-form-data';
import type {
  CreateSupportTicketMessagePayload,
  CreateSupportTicketMessageResult,
  FetchSupportTicketsParams,
  SupportTicketAttachmentUrl,
  SupportTicketDetail,
  SupportTicketListItem,
  UpdateSupportTicketStatusPayload,
} from './models';

const BASE_PATH = `${BASE}${API}${V1}${URL_TENANTS}`;

const buildUrl = (tenantId: string, suffix = '') =>
  `${BASE_PATH}/${tenantId}${URL_SUPPORT_TICKETS}${suffix}`;

export const createSupportTicket = async (
  tenantId: string,
  data: CreateSupportTicketDto,
  files: File[] = []
): Promise<CreateSupportTicketResultDto> => {
  const response = await client<CreateSupportTicketResultDto>({
    url: buildUrl(tenantId),
    method: 'POST',
    data: buildSupportTicketFormData(data, files),
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const fetchSupportTickets = async (
  tenantId: string,
  params?: FetchSupportTicketsParams
): Promise<ListResponse<SupportTicketListItem>> => {
  const response = await client<ListResponse<SupportTicketListItem>>({
    url: buildUrl(tenantId),
    method: 'GET',
    params,
  });
  return response.data;
};

export const fetchSupportTicket = async (
  tenantId: string,
  ticketId: string
): Promise<SupportTicketDetail> => {
  const response = await client<SupportTicketDetail>({
    url: buildUrl(tenantId, `/${ticketId}`),
    method: 'GET',
  });
  return response.data;
};

/**
 * Risposta del cliente nel thread. Sempre multipart, anche senza allegati: il
 * backend legge testo e file dallo stesso interceptor, e due forme di richiesta
 * per lo stesso endpoint sarebbero due strade da tenere allineate.
 */
export const createSupportTicketMessage = async (
  tenantId: string,
  ticketId: string,
  { body, files = [] }: CreateSupportTicketMessagePayload
): Promise<CreateSupportTicketMessageResult> => {
  const response = await client<CreateSupportTicketMessageResult>({
    url: buildUrl(tenantId, `/${ticketId}${URL_SUPPORT_MESSAGES}`),
    method: 'POST',
    data: buildSupportTicketMessageFormData(body, files),
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateSupportTicketStatus = async (
  tenantId: string,
  ticketId: string,
  data: UpdateSupportTicketStatusPayload
): Promise<SupportTicketDetail> => {
  const response = await client<SupportTicketDetail>({
    url: buildUrl(tenantId, `/${ticketId}${URL_SUPPORT_STATUS}`),
    method: 'PATCH',
    data,
  });
  return response.data;
};

/**
 * URL firmato di un allegato. Scade (`expiresInSeconds`): si chiede al click,
 * non al render della lista, altrimenti si distribuiscono link gia' morti.
 */
export const fetchSupportTicketAttachmentUrl = async (
  tenantId: string,
  ticketId: string,
  attachmentId: string
): Promise<SupportTicketAttachmentUrl> => {
  const response = await client<SupportTicketAttachmentUrl>({
    url: buildUrl(
      tenantId,
      `/${ticketId}${URL_SUPPORT_ATTACHMENTS}/${attachmentId}${URL_SUPPORT_DOWNLOAD}`
    ),
    method: 'GET',
  });
  return response.data;
};

// --- [Tecnos] Slot e upload allegati via Gestify (IN PAUSA) ----------------
// Le funzioni seguenti chiamano endpoint Tecnos disattivati nell'interim (vedi
// support-tickets.controller.ts). Restano nel repo, ma non sono piu' invocate:
// gli allegati ora viaggiano nel create multipart e la formazione non usa slot.
// ---------------------------------------------------------------------------

export const fetchSupportSlotDays = async (
  tenantId: string,
  tipo: SupportTicketType,
  dataInizio: string,
  dataFine: string
): Promise<SlotDayResponse[]> => {
  const response = await client<SlotDayResponse[]>({
    url: buildUrl(tenantId, URL_SUPPORT_SLOTS_DAYS),
    method: 'GET',
    params: { tipo, dataInizio, dataFine },
  });
  return response.data;
};

export const fetchSupportSlotHours = async (
  tenantId: string,
  tipo: SupportTicketType,
  dataRiferimento: string
): Promise<SlotHourResponse[]> => {
  const response = await client<SlotHourResponse[]>({
    url: buildUrl(tenantId, URL_SUPPORT_SLOTS_HOURS),
    method: 'GET',
    params: { tipo, dataRiferimento },
  });
  return response.data;
};

export const requestSupportAttachmentUpload = async (
  tenantId: string,
  ticketId: number,
  data: UploadRequestDto
): Promise<UploadRequestResponse> => {
  const response = await client<UploadRequestResponse>({
    url: buildUrl(
      tenantId,
      `/${ticketId}${URL_SUPPORT_ATTACHMENTS}${URL_SUPPORT_ATTACHMENT_REQUEST}`
    ),
    method: 'POST',
    data,
  });
  return response.data;
};

export const finalizeSupportAttachmentUpload = async (
  tenantId: string,
  ticketId: number,
  data: UploadFinalizeDto
): Promise<UploadFinalizeResponse> => {
  const response = await client<UploadFinalizeResponse>({
    url: buildUrl(
      tenantId,
      `/${ticketId}${URL_SUPPORT_ATTACHMENTS}${URL_SUPPORT_ATTACHMENT_FINALIZE}`
    ),
    method: 'POST',
    data,
  });
  return response.data;
};
